import React, { useState } from 'react';

const RaiseComplaint = () => {
  const [text, setText] = useState('');
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  // Validate form before submission
  const validateForm = () => {
    if (!checked) {
      setFormError("Please confirm your complaint is genuine.");
      return false;
    }
    if (!text.trim()) {
      setFormError("Complaint text cannot be empty.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Debug log
      console.log("Sending complaint data:", text);

      // Send complaint data to backend API
      const axios = require('axios');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/raise/complaint`,
        {
          complaint: text
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Complaint response:", response);

      if (response.status === 400) {
        setFormError("Complaint already exists or invalid. Please try again.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (response.status === 200) {
        setMessage("Complaint submitted successfully!");
        setText("");
        setChecked(false);
      }

      if (!response.data.success) {
        setFormError(
          response.data.message || "Complaint failed. Please try again."
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      setFormError(
        error.response?.data?.message ||
        "Complaint failed. Please try again."
      );
      if (error.response && error.response.status === 400) {
        // Show some error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                <span className="text-4xl">🗣️</span>
                Raise Your Voice
              </h2>
              <p className="text-indigo-100 text-lg">
                Share your thoughts, concerns, or complaints with us
              </p>
            </div>
          </div>

          {/* Form Section */}
          <div className="px-8 py-8">
            {/* Error Alert */}
            {formError && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                <div className="flex items-center">
                  <span className="text-red-400 text-xl mr-3">⚠️</span>
                  <p className="text-red-800 font-medium">{formError}</p>
                </div>
              </div>
            )}

            {/* Success Alert */}
            {message && (
              <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
                <div className="flex items-center">
                  <span className="text-green-400 text-xl mr-3">✅</span>
                  <p className="text-green-800 font-medium">{message}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Textarea Field */}
              <div>
                <label htmlFor="complaintText" className="block text-sm font-semibold text-gray-700 mb-3">
                  Your Complaint/Thoughts
                </label>
                <textarea
                  id="complaintText"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Share your thoughts, concerns, or complaints in detail..."
                  rows={6}
                  maxLength={1000}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none transition-all duration-200 resize-none bg-gray-50 focus:bg-white text-gray-800 placeholder-gray-400 text-base leading-relaxed"
                />
              </div>

              {/* Checkbox */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => setChecked(e.target.checked)}
                    id="agreeCheckbox"
                    className="h-5 w-5 text-indigo-600 border-2 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 transition-all duration-200"
                  />
                </div>
                <label htmlFor="agreeCheckbox" className="text-sm text-gray-700 font-medium leading-relaxed cursor-pointer">
                  I confirm that my complaint is genuine and I take responsibility for its content
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={!checked || !text.trim() || isSubmitting}
                  className={`w-full flex items-center justify-center px-6 py-4 rounded-xl text-white font-semibold text-lg transition-all duration-300 transform ${!checked || !text.trim() || isSubmitting
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:-translate-y-1 hover:shadow-lg active:translate-y-0'
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <span className="mr-2 text-xl">📤</span>
                      Submit Complaint
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
            <p className="text-center text-sm text-gray-500">
              Your complaint will be reviewed and processed accordingly
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaiseComplaint;
